/**
 * Hero CTA — message-first (Wave H1) + its instrumentation (Wave H0).
 *
 * Everything in here is pure (no React, no env reads, no DOM) so the same
 * predicates run in the client shell, in the public call route, in the SMS
 * composer (the `#REF` parser), in the edge middleware, and in node-env unit
 * tests. The component layer only wires DOM inputs into these functions.
 *
 * Vocabulary:
 *  - variant: which arm the visitor sees — `message` (Message Tessa → sms:/
 *    wa.me) or `call` (today's phone form, "Tessa calls YOU").
 *  - aid: the anonymous visitor id (`apr_aid` cookie, lib/onboarding/anon-id)
 *    that buckets the visitor deterministically — a visitor never flips arms
 *    between pages or visits, which is what makes "claims per distinct viewer"
 *    measurable.
 *  - ref: a 6-char attribution tag (`M`|`C` + 5 Crockford-base32 chars) minted
 *    per hero mount and carried in the prefilled body (`Hi Tessa #M7K2QX`)
 *    and in the call POST. It is the join key between the anonymous view/click
 *    events and the `onboarding_calls.campaign='hero:<ref>'` row the first
 *    inbound (or the dial) writes — top of funnel meets bottom of funnel
 *    without ever identifying the visitor.
 */

import type { SiteLocale } from '@/lib/marketing/page-metadata';
import {
  DEFAULT_SITE_LOCALE,
  TRANSLATED_LOCALES,
  type TranslatedLocale,
} from '@/lib/marketing/locale-routing';

export type HeroCtaVariant = 'message' | 'call';

/**
 * Why a variant resolved the way it did. `override` is the QA knob
 * (`?cta=message|call`) — the readout SQL EXCLUDES it so founder smoke
 * tests never count as traffic. `doorway` is the same forced arm but
 * arriving through a link that CARRIES a ref (`?cta=call&ref=C…`, the email
 * doorway's call door): real traffic, counted, and the carried ref is what
 * joins the hero events and the call row back to the hand-off that sent the
 * visitor. The rest are all "real" and aggregate together: `bucket` (hashed
 * split), `forced` (pct 0/100), `no_number` (message arm impossible — no
 * SMS/WhatsApp number configured), `no_call` (call arm impossible — the
 * call flag is off).
 */
export type HeroCtaVariantSource = 'bucket' | 'forced' | 'override' | 'doorway' | 'no_number' | 'no_call';

export const HERO_CTA_VARIANT_SOURCES: readonly HeroCtaVariantSource[] = [
  'bucket',
  'forced',
  'override',
  'doorway',
  'no_number',
  'no_call',
];

export type HeroCtaDevice = 'mobile' | 'desktop';

/** One id per placement so the readout can split hero vs closer on each
 *  page. The industry pages (/for/*, every language) share one pair; the
 *  Spanish and French homes report as home_*. The payload's `locale` splits
 *  each surface by page language. Required at
 *  every mount (typed, not stringly). */
export type HeroCtaSurface =
  | 'home_hero'
  | 'home_closer'
  | 'teams_hero'
  | 'teams_closer'
  | 'dev_hero'
  | 'dev_closer'
  | 'industry_hero'
  | 'industry_closer';

export const HERO_CTA_SURFACES: readonly HeroCtaSurface[] = [
  'home_hero',
  'home_closer',
  'teams_hero',
  'teams_closer',
  'dev_hero',
  'dev_closer',
  'industry_hero',
  'industry_closer',
];

/** Every action the hero can report. `call_reveal` is reserved by the plan
 *  for a future "prefer a call?" disclosure — not rendered today (Camilo:
 *  the call is asked for inside the thread, never offered on the hero). */
export type HeroCtaChannel = 'sms' | 'whatsapp' | 'call' | 'call_reveal' | 'copy_number' | 'qr_shown';

export const HERO_CTA_CHANNELS: readonly HeroCtaChannel[] = [
  'sms',
  'whatsapp',
  'call',
  'call_reveal',
  'copy_number',
  'qr_shown',
];

// ─── Variant resolution ──────────────────────────────────────────────────────

/** Salt so a future experiment on the same cookie gets an independent split
 *  (bump the suffix, every visitor re-buckets). */
const BUCKET_SALT = ':hero-cta-v1';

/** FNV-1a 32-bit — tiny, deterministic, good enough spread over uuids for a
 *  percentage split (the test pins 50% over 2 000 ids inside 45–55%). */
export function fnv1a32(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619) >>> 0; // × FNV prime, 32-bit wrap
  }
  return hash >>> 0;
}

/** 0–99 bucket for an anonymous id. */
export function heroCtaBucket(aid: string): number {
  return fnv1a32(`${aid}${BUCKET_SALT}`) % 100;
}

/** Clamp the env value to an integer 0–100; anything unparseable is 0
 *  (= call-first, the byte-identical rollback). */
export function readHeroCtaPct(raw: string | undefined | null): number {
  if (raw == null) return 0;
  const n = Number(String(raw).trim());
  if (!Number.isFinite(n)) return 0;
  return Math.min(100, Math.max(0, Math.floor(n)));
}

/** `?cta=message|call` — anything else is no override. */
export function parseCtaOverride(raw: string | null | undefined): HeroCtaVariant | null {
  const v = (raw ?? '').trim().toLowerCase();
  return v === 'message' || v === 'call' ? v : null;
}

export interface ResolveHeroCtaVariantArgs {
  /** Anonymous id (uuid). Empty/unknown → bucket 0 via a fixed string, so the
   *  resolver still answers deterministically (pct 100 → message, else call). */
  aid: string | null | undefined;
  /** 0–100 share of visitors who get the message arm. */
  pct: number;
  /** QA override from the query string (render-only, never persisted). */
  override?: HeroCtaVariant | null;
  /** A ref carried in the URL (`?ref=C7K2QX`, already clamped by
   *  `normalizeHeroRef`). With an override whose arm matches the ref's
   *  letter, the arrival is attributed traffic (`doorway`), not QA. */
  urlRef?: string | null;
  /** An SMS or WhatsApp number is configured (the message arm can render). */
  hasMessageNumber: boolean;
  /** The call flag is on (the call arm can render). Defaults to true. */
  callEnabled?: boolean;
}

export interface ResolvedHeroCtaVariant {
  variant: HeroCtaVariant;
  source: HeroCtaVariantSource;
  /** The ref to report instead of minting one — set only for `doorway`. */
  carriedRef?: string;
}

/**
 * Precedence (highest first):
 *   1. capability — an arm that cannot render is never chosen (no message
 *      number → call; call off → message). Wins even over the QA override:
 *      `?cta=message` with no number would render nothing.
 *   2. override — `?cta=…`, tagged so the readout can exclude it; with a
 *      URL ref whose letter matches the arm it is `doorway` (attributed
 *      traffic, the ref carried instead of minted).
 *   3. pct 0 / 100 — forced, no hashing (unset flag = 0 = call-first).
 *   4. hashed bucket < pct → message.
 */
export function resolveHeroCtaVariant(args: ResolveHeroCtaVariantArgs): ResolvedHeroCtaVariant {
  const callEnabled = args.callEnabled ?? true;
  if (!args.hasMessageNumber) return { variant: 'call', source: 'no_number' };
  if (!callEnabled) return { variant: 'message', source: 'no_call' };
  if (args.override) {
    const ref = args.urlRef ?? null;
    if (ref && HERO_REF_RE.test(ref) && refVariant(ref) === args.override) {
      return { variant: args.override, source: 'doorway', carriedRef: ref };
    }
    return { variant: args.override, source: 'override' };
  }

  const pct = readHeroCtaPct(String(args.pct));
  if (pct <= 0) return { variant: 'call', source: 'forced' };
  if (pct >= 100) return { variant: 'message', source: 'forced' };

  const bucket = heroCtaBucket(args.aid || 'anonymous');
  return { variant: bucket < pct ? 'message' : 'call', source: 'bucket' };
}

/**
 * What the STATIC HTML renders before the cookie is known (the pages stay
 * static, so the variant is resolved client-side post-hydration). Crawlers
 * and no-JS readers see this arm. Rules: an arm that cannot render is never
 * SSR'd; pct 0 (unset) keeps today's call form byte-for-byte (the instant
 * rollback); any positive pct SSRs the message arm — at 100 that IS the
 * final render for everyone, and in a split it is the arm the copy leads
 * with. Bucketed `call` visitors swap post-hydration inside a same-height
 * row (zero CLS by construction: both arms are one h-12 row + a 13px line).
 */
export function ssrHeroCtaVariant(args: {
  pct: number;
  hasMessageNumber: boolean;
  callEnabled: boolean;
}): HeroCtaVariant {
  if (!args.hasMessageNumber) return 'call';
  if (!args.callEnabled) return 'message';
  return readHeroCtaPct(String(args.pct)) <= 0 ? 'call' : 'message';
}

// ─── Device detection ────────────────────────────────────────────────────────

export type MobilePlatform = 'ios' | 'android' | 'other';

/**
 * "Will this OS hand an `sms:` link to a messaging app?" — a UA question,
 * deliberately NOT a viewport one (a narrow desktop window still has no SMS
 * app; a landscape phone still does). iPadOS ≥13 ships a Macintosh UA, so a
 * Mac with more than one touch point is an iPad (Macs report 0).
 */
export function detectMobilePlatform(ua: string | null | undefined, maxTouchPoints = 0): MobilePlatform {
  const s = ua ?? '';
  if (/iPhone|iPod|iPad/i.test(s)) return 'ios';
  if (/Macintosh/i.test(s) && maxTouchPoints > 1) return 'ios';
  if (/Android/i.test(s)) return 'android';
  return 'other';
}

export function detectMobile(ua: string | null | undefined, maxTouchPoints = 0): boolean {
  const s = ua ?? '';
  if (/iPhone|iPod|iPad|Android|Windows Phone|IEMobile|BlackBerry|BB10|webOS|Opera Mini/i.test(s)) {
    return true;
  }
  // iPadOS desktop-class UA: "Macintosh" + touch.
  if (/Macintosh/i.test(s) && maxTouchPoints > 1) return true;
  return false;
}

// ─── Deep links ──────────────────────────────────────────────────────────────

/**
 * `sms:` URI with a prefilled body. The separator is the whole bug this file
 * fixes: iOS wants `sms:+1…&body=` (a `?` leaves the body blank on older
 * iOS), Android wants the RFC 5724 `sms:+1…?body=`. For an unknown scanner
 * (the desktop QR) the `?&body=` form is parsed by both — iOS reads the
 * `&body`, Android reads the query. `#` MUST be percent-encoded or the ref
 * becomes a URI fragment and never reaches the message.
 */
export function buildSmsHref(numberE164: string, body: string, platform: MobilePlatform = 'other'): string {
  const number = numberE164.trim();
  const encoded = encodeURIComponent(body);
  if (platform === 'ios') return `sms:${number}&body=${encoded}`;
  if (platform === 'android') return `sms:${number}?body=${encoded}`;
  return `sms:${number}?&body=${encoded}`;
}

/** wa.me wants bare digits (no `+`); the prefilled text opens Meta's 24h
 *  session window so the composer's reply needs no template. */
export function buildWhatsAppHref(numberE164: string, body: string): string {
  const digits = numberE164.replace(/\D/g, '');
  return `https://wa.me/${digits}?text=${encodeURIComponent(body)}`;
}

// ─── Attribution ref ─────────────────────────────────────────────────────────

/** Crockford base32: no I, L, O, U — the lookalike classes a human might
 *  "correct" while retyping from a QR they could not scan. */
const REF_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const REF_BODY_LEN = 5;

/** Exact ref shape, uppercase: variant letter + 5 Crockford chars. */
export const HERO_REF_RE = /^[MC][0-9A-HJKMNP-TV-Z]{5}$/;

/** The tag as it appears inside message text (`… #M7K2QX`). Case-insensitive
 *  so a retyped lowercase tag still attributes; bounded so `#M7K2QX9` (a 7th
 *  alphanumeric) is NOT a ref — that is some other token. */
export const HERO_REF_TAG_RE = /#([MCmc][0-9A-HJKMNP-TV-Za-hjkmnp-tv-z]{5})(?![0-9A-Za-z])/;

/** The prefilled greeting per page language. */
export const HERO_GREETING: Record<SiteLocale, string> = {
  en: 'Hi Tessa',
  es: 'Hola Tessa',
  fr: 'Bonjour Tessa',
};

/**
 * The prefilled first message, in the language of the page it was sent from.
 * English pages say "Hi Tessa" on purpose (2026-09-04): the composer's model
 * mirrors the visitor's language, and "Hola Tessa" was steering English-
 * speaking visitors into a Spanish interview. A /es or /fr page greets in its
 * own language, which heroGreetingLang() turns into the thread's language.
 */
export function heroBody(ref: string | null | undefined, locale: SiteLocale = DEFAULT_SITE_LOCALE): string {
  const greeting = HERO_GREETING[locale];
  return ref ? `${greeting} #${ref}` : greeting;
}

/**
 * The page language a hero-tagged first message was sent from, when its text
 * is exactly a translated page's greeting (tag already stripped). English and
 * anything rewritten return null, so the phone country keeps deciding there.
 */
export function heroGreetingLang(stripped: string): TranslatedLocale | null {
  const said = stripped.trim().replace(/[\s.!¡,]+$/u, '').toLowerCase();
  return TRANSLATED_LOCALES.find((l) => HERO_GREETING[l].toLowerCase() === said) ?? null;
}

/** Mint a ref. `random` is injectable for tests; defaults to crypto when the
 *  runtime has it (browser/edge/node ≥19), else Math.random — the ref is an
 *  attribution tag, not a secret. */
export function mintHeroRef(variant: HeroCtaVariant, random: () => number = defaultRandom): string {
  let out = variant === 'message' ? 'M' : 'C';
  for (let i = 0; i < REF_BODY_LEN; i++) {
    const idx = Math.min(REF_ALPHABET.length - 1, Math.floor(random() * REF_ALPHABET.length));
    out += REF_ALPHABET[idx];
  }
  return out;
}

function defaultRandom(): number {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.getRandomValues === 'function') {
    const buf = new Uint32Array(1);
    c.getRandomValues(buf);
    return buf[0] / 0x1_0000_0000;
  }
  return Math.random();
}

/** Clamp an untrusted ref (query/body param) to the exact shape, uppercased.
 *  Null for anything else — the route never stores a caller-chosen string. */
export function normalizeHeroRef(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const up = raw.trim().toUpperCase();
  return HERO_REF_RE.test(up) ? up : null;
}

export interface ParsedHeroRef {
  /** Uppercased ref, e.g. `M7K2QX`. */
  ref: string;
  /** The variant letter decoded: M → message, C → call. */
  variant: HeroCtaVariant;
  /** The text with the tag removed and whitespace collapsed — what the
   *  composer stores in `turns` (the model never sees the tag). */
  stripped: string;
}

/** Find the first `#REF` tag in an inbound message. The composer calls this
 *  on the first inbound of a NEW row → `campaign='hero:<ref>'`. */
export function parseHeroRef(text: string | null | undefined): ParsedHeroRef | null {
  if (!text) return null;
  const m = HERO_REF_TAG_RE.exec(text);
  if (!m) return null;
  const ref = m[1].toUpperCase();
  const stripped = text.replace(m[0], '').replace(/\s{2,}/g, ' ').trim();
  return { ref, variant: ref[0] === 'M' ? 'message' : 'call', stripped };
}

/** The arm a ref was minted for: M → message, C → call. */
export function refVariant(ref: string): HeroCtaVariant {
  return ref[0]?.toUpperCase() === 'M' ? 'message' : 'call';
}

/** `onboarding_calls.campaign` value for a hero ref. */
export function heroCampaign(ref: string): string {
  return `hero:${ref}`;
}

/** The ref inside an `onboarding_calls.campaign` value, or null when the
 *  campaign is not a hero stamp. */
export function refFromCampaign(campaign: string | null | undefined): string | null {
  if (!campaign || !campaign.startsWith('hero:')) return null;
  return normalizeHeroRef(campaign.slice('hero:'.length));
}
